import {
  MinusCircleOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Divider,
  message,
  Space,
  Spin,
  Tooltip,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import Form from 'antd/lib/form';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import orderApi from '../../apis/order';
import {
  ICreateOrderDesign,
  ICreateOrderDesignPlacement,
  ICreateOrderRequest,
  IDesign,
  IOrder,
  IOrderDesign,
  IOrderItem,
  IPrintArea,
  IProductTemplate,
  IProductTemplateVariant,
  OrderStatus,
  OrderType,
  Platform,
  PrintAreaType,
  ProductType,
  Side,
} from '../../interfaces/Order';
import {
  ButtonGroupContainer,
  FlexContainer,
  PageContainer,
} from '../../styles/Global';
import CustomForm from '../Form/Form';
import CustomInput from '../Form/FormItem/Input/Input';
import SearchDesignModal from '../Modal/SearchDesignModal';
import PageHeader from '../PageHeader/PageHeader';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import {
  CheckoutDraftRow,
  CreateOrderItemWrapper,
  ImageContainer,
  SpinContainer,
  StyledInputNumber,
  StyledSelect,
} from './Order.styled';

import { RootState } from '../../reducers';
import { IAuthState } from '../../reducers/auth';
import { Role } from '../../role/role';
import ConfirmModal from '../Modal/ConfirmModal';
// import RequestResendModal from '../Modal/RequestResendModal';
// import PhoneCaseEditor from '../PhoneCaseEditor/PhoneCaseEditor';
import { productTemplateApi } from '../../apis/productTemplate';
import { ProductSizeMap } from '../../constants/order';
import { PaginationParamsResponse } from '../../interfaces/PaginationParamsResponse';
import TShirtMockupEditor from '../TShirtMockupEditor/TShirtMockupEditor';
import CustomTextArea from '../TextArea/TextArea';

const OrderDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{
    id?: string;
  }>();

  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const [order, setOrder] = useState<Partial<IOrder>>({
    orderId: '',
    trackingCode: '',
    address: '',
    trackingReference: '',
    designs: [], // Legacy field for backward compatibility
    orderDesigns: [], // New placement-based structure
  });
  const [visibleModal, setVisibleModal] = useState(false);
  const [listDesign, setListDesign] = useState<string[]>([]);
  const [currentDesignIdx, setCurrentDesignIdx] = useState(0);
  const [status, setStatus] = useState(OrderStatus.PENDING);
  const [sellerOrderId, setSellerOrderId] = useState<string>();
  const [showCanvas, setShowCanvas] = useState<Record<number, boolean>>({});
  // const [phoneModel, setPhoneModel] = useState<string>();
  // const [totalPhoneCaseCount] = useState(1);
  const { currentUser } = useSelector(
    (state: RootState) => state.auth as IAuthState
  );
  const isAdmin = currentUser?.role === Role.ADMIN;
  const [total, setTotal] = useState(0);
  // const [visibleRequestModal, setVisibleRequestModal] = useState(false);
  // const [disableTracking, setDisableTracking] = useState(false);
  const [confirmationModalVisible, setConfirmationModalVisible] =
    useState(false);
  // const [pendingFormValues, setPendingFormValues] = useState<any>(null);
  const [trackingFileList, setTrackingFileList] = useState<UploadFile[]>([]);
  const [uploadingTracking, setUploadingTracking] = useState(false);
  // Backup state to store tracking reference URL independently of form
  const [trackingReferenceUrl, setTrackingReferenceUrl] = useState<string>('');
  const [uploadingMockups, setUploadingMockups] = useState<
    Record<number, boolean>
  >({});
  const [mockupUrls, setMockupUrls] = useState<Record<number, string>>({});
  // Track placement data for each design item
  const [designPlacements, setDesignPlacements] = useState<
    Record<number, ICreateOrderDesignPlacement[]>
  >({});
  const [disabledAddDesign, setDisabledAddDesign] = useState(false);
  // Track which editors are visible for each design item
  const [visibleEditors, setVisibleEditors] = useState<
    Record<number, { front: boolean; back: boolean }>
  >({});
  // Force re-renders when form values change to update button states
  Form.useWatch([], form);
  const [productTemplate, setProductTemplate] =
    useState<PaginationParamsResponse<IProductTemplate>>();
  const [productTemplateVariants, setProductTemplateVariants] =
    useState<Array<IProductTemplateVariant>>();
  const [printAreas, setPrintAreas] = useState<IPrintArea[]>([]);
  // const params = useParams();

  const isFormDisabled = useMemo(() => {
    return !isAdmin && !!id && status !== OrderStatus.PENDING;
  }, [isAdmin, id, status]);

  // This will be moved after fetchOrder declaration

  useEffect(() => {
    // Initialize with one design item that can have multiple placements
    const initialDesign = {
      quantity: 1,
      itemCost: 0,
      placements: [], // Start with empty placements array
    };

    setOrder((prevOrder) => ({
      ...prevOrder,
      designs: [initialDesign], // For backward compatibility
    }));
    form.setFieldValue('designs', [initialDesign]);

    // Initialize placement state
    setDesignPlacements({
      0: [], // First design item starts with no placements
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally only run once on mount

  useEffect(() => {
    // if (!!phoneModel) {
    //   handleModelChange();
    // }
    handleTotal({}, form.getFieldsValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listDesign]); // handleTotal and form are stable references

  useEffect(() => {
    // Set product template variants when order and product templates are loaded
    if (order.designs && order.designs.length > 0 && productTemplate?.items) {
      const firstDesign = order.designs[0];
      if (firstDesign.productTemplateId) {
        const selectedProductTemplate = productTemplate.items.find(
          (pt) => pt.id === firstDesign.productTemplateId
        );
        if (selectedProductTemplate) {
          setProductTemplateVariants(selectedProductTemplate.variants);
        }
      }
    }
  }, [order.designs, productTemplate]);

  const calculateTotal = useCallback(() => {
    const designs: IOrderDesign[] = form.getFieldValue('designs');

    if (!designs) {
      return 0;
    }

    const total = designs.reduce(
      (accumulator, currentValue) =>
        accumulator + (currentValue?.itemCost || 0),
      0
    );

    return total;
  }, [form]);

  const fetchOrder = useCallback(
    async (id: string) => {
      if (!id) return;

      try {
        setLoading(true);
        const result = await orderApi.getOrderDetail(id);

        const {
          orderId,
          trackingCode,
          address,
          trackingReference,
          orderDesigns,
          totalCost,
          status,
          notes,
          internalNotes,
          platform,
          orderType,
          // resendRequest,
        } = result;
        let newListDesign: string[] = [];
        const tmp = orderDesigns?.map((d: any) => {
          // Extract design URLs from placements
          const placementDesigns = d?.placements || [];
          placementDesigns.forEach((placement: any) => {
            const designObj =
              typeof placement.design === 'string' ? null : placement.design;
            if (designObj && 'designUrl' in designObj && designObj.designUrl) {
              newListDesign?.push(
                designObj.thumbnailUrl ??
                  designObj.originalFileUrl ??
                  designObj.designUrl
              );
            }
          });

          // For backward compatibility, also check direct design property
          const directDesignObj =
            typeof d.design === 'string' ? null : d.design;
          if (
            directDesignObj &&
            'designUrl' in directDesignObj &&
            directDesignObj.designUrl
          ) {
            newListDesign?.push(
              directDesignObj.thumbnailUrl ??
                directDesignObj.originalFileUrl ??
                directDesignObj.designUrl
            );
          }

          // Create legacy-compatible structure for form
          const mappedItem = {
            itemCost: d?.itemCost || 0,
            quantity: d?.quantity,
            id: d?.id,
            productTemplateId:
              d?.productTemplateVariant?.productTemplate?.id ||
              d?.productTemplate?.id ||
              d?.productTemplateId,
            productTemplateVariantId:
              d?.productTemplateVariant?.id || d?.productTemplateVariantId,
            // For backward compatibility, use first placement if available
            // Store placement data for new structure
            placements: placementDesigns.map((placement: any) => ({
              designId:
                typeof placement.design === 'string'
                  ? placement.design
                  : placement.design?.id,
              side: placement.side || Side.FRONT, // Default to front if not specified
              printAreaId: placement.printAreaId || placement.printArea?.id,
              mockupUrl: placement.mockupUrl,
            })) as ICreateOrderDesignPlacement[],
          } as IOrderItem;

          return mappedItem;
        });

        setListDesign(newListDesign);

        // Extract mockup URLs and set them in state
        const newMockupUrls: Record<number, string> = {};
        const newDesignPlacements: Record<
          number,
          ICreateOrderDesignPlacement[]
        > = {};

        tmp?.forEach((design, index) => {
          design.placements?.forEach((placement) => {
            if (placement.mockupUrl) {
              newMockupUrls[index] = placement.mockupUrl;
            }
          });

          // Store placement data for each design
          if ((design as any).placements) {
            newDesignPlacements[index] = (design as any).placements;
          } else {
            // For backward compatibility, create single placement if needed
            newDesignPlacements[index] = [];
          }
        });

        setMockupUrls(newMockupUrls);
        setDesignPlacements(newDesignPlacements);

        setOrder({
          orderId,
          trackingCode,
          address,
          trackingReference,
          notes,
          internalNotes,
          designs: tmp,
          totalCost,
          platform,
          orderType,
          // resendRequest,
          status,
          id,
        } as Partial<IOrder>);
        setSellerOrderId(orderId);
        setStatus(status as OrderStatus);
        form.setFieldsValue({
          orderId,
          trackingCode,
          address,
          trackingReference,
          notes,
          internalNotes,
          designs: tmp,
          status,
          platform,
          orderType,
        });

        // If there's an existing tracking reference (file URL), show it in the file list
        if (trackingReference) {
          setTrackingFileList([
            {
              uid: '1',
              name: 'tracking-label',
              status: 'done',
              url: trackingReference,
            },
          ]);
          setTrackingReferenceUrl(trackingReference); // Set backup state
        } else {
        }
        const tmpTotal = calculateTotal();
        setTotal(totalCost ?? tmpTotal);
      } catch (error) {
        message.error('Failed to load order details. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [calculateTotal, form]
  ); // Include all dependencies used within fetchOrder

  // Effect to load data on component mount or when id changes
  useEffect(() => {
    fetchProductTemplates();
    fetchPrintAreas();
    if (!!id) {
      fetchOrder(id);
    }
  }, [id, fetchOrder]);

  const updateOrder = async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      const values = form.getFieldsValue(true);
      const {
        orderId,
        trackingCode,
        address,
        trackingReference,
        designs,
        status,
        notes,
        internalNotes,
        platform,
        orderType,
      } = values;

      // Transform the designs to use the new placement-based structure
      const transformedDesigns: ICreateOrderDesign[] =
        designs?.map((d: any, index: number) => {
          const placements = designPlacements[index] || [];

          return {
            productTemplateVariantId: d.productTemplateVariantId,
            quantity: d.quantity,
            placements: placements,
            id: d.id, // For updates
          };
        }) || [];

      const payload = {
        orderId,
        trackingCode,
        address,
        trackingReference,
        designs: transformedDesigns,
        status,
        notes,
        internalNotes,
        platform,
        orderType,
      };

      const updatedOrder = await orderApi.updateOrder(id, payload as any);
      setStatus(status);

      // Refresh order data without navigating away
      if (updatedOrder) {
        // Update state with the returned data
        setOrder({
          ...order,
          ...updatedOrder,
        });
        message.success('Order updated successfully!');
      } else {
        // Fallback to fetching the full order again
        fetchOrder(id);
        message.success('Order updated successfully!');
      }
    } catch (e) {
      message.error('Update order failed');
    } finally {
      setLoading(false);
    }
  };

  const deleteOrder = async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      await orderApi.deleteOrder([id]);
      message.success('Delete order success!');
      navigate(-1);
    } catch (e) {
      message.error('Delete order fail!');
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async () => {
    try {
      setLoading(true);
      const values = form.getFieldsValue(true);

      const payload = values;

      // Ensure trackingReference is included if it was uploaded

      // Use backup state as primary source if available
      if (trackingReferenceUrl && !payload.trackingReference) {
        payload.trackingReference = trackingReferenceUrl;
      }

      // Fallback: try form field value
      if (!payload.trackingReference) {
        const trackingRef = form.getFieldValue('trackingReference');
        if (trackingRef) {
          payload.trackingReference = trackingRef;
        }
      }

      // Last resort: check trackingFileList
      if (!payload.trackingReference && trackingFileList.length > 0) {
        const uploadedFile = trackingFileList.find(
          (file) => file.status === 'done' && file.url
        );
        if (uploadedFile && uploadedFile.url) {
          payload.trackingReference = uploadedFile.url;
        }
      }

      // Transform the designs to use the new placement-based structure
      const transformedDesigns: ICreateOrderDesign[] =
        payload.designs?.map((d: any, index: number) => {
          const placements = designPlacements[index] || [];

          return {
            productTemplateVariantId: d.productTemplateVariantId,
            quantity: d.quantity,
            placements: placements,
            id: d.id, // For updates
          };
        }) || [];

      // Replace the legacy designs with the new structure
      payload.designs = transformedDesigns;

      // Final check for trackingReference before API call
      if (!payload.trackingReference) {
        // Last resort: if we have any evidence of tracking upload, try to include it
        if (trackingFileList.length > 0) {
          const trackingRef = form.getFieldValue('trackingReference');
          const fileRef = trackingFileList.find(
            (f) => f.status === 'done'
          )?.url;
          const finalRef = trackingRef || fileRef;

          if (finalRef) {
            payload.trackingReference = finalRef;
          }
        }
      }

      // Validate tracking reference is required for certain operations
      if (!payload.trackingReference && !id) {
        message.error('Vui lòng tải lên mã vận chuyển trước khi tạo đơn hàng');
        return;
      }

      if (!payload.orderType) {
        payload.orderType = OrderType.NORMAL;
      }

      const createdOrder = await orderApi.createOrder(
        payload as ICreateOrderRequest
      );

      if (!!id) {
        message.success('Order created successfully!');
        // Navigate to edit page for the newly created order
        navigate(`/order/${createdOrder.id}/edit`, {
          replace: true,
        });
      } else {
        message.success('Order created successfully!');
        navigate(-1);
      }
    } catch (e) {
      console.error(e);
      message.error((e as any)?.message || 'Create order failed');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductTemplates = async () => {
    try {
      const result = await productTemplateApi.getProductTemplates();
      setProductTemplate(result);
    } catch (e) {
      message.error('Failed to load product template. Please try again.');
    }
  };

  const fetchPrintAreas = async () => {
    try {
      const result = await orderApi.getPrintAreas({
        page: 1,
        perPage: 1000,
      });

      setPrintAreas(result.items);
    } catch (e) {
      message.error('Failed to load print areas. Please try again.');
    }
  };

  const calculateItemCost = (designItem: IOrderItem, designIndex?: number) => {
    const { productTemplateVariantId, quantity } = designItem;

    if (!productTemplateVariantId || !quantity) {
      return 0;
    }

    // Find the product template variant price
    let variantPrice = 0;
    if (productTemplate?.items) {
      for (const template of productTemplate.items) {
        const variant = template.variants?.find(
          (v) => v.id === productTemplateVariantId
        );
        if (variant) {
          variantPrice = variant.price || 0;
          break;
        }
      }
    }

    // Calculate total print area costs for all placements
    let totalPrintAreaPrice = 0;
    if (designIndex !== undefined) {
      const placements = designPlacements[designIndex] || [];
      totalPrintAreaPrice = placements.reduce((total, placement) => {
        const printAreaObj = printAreas.find(
          (pa) => pa.id === placement.printAreaId
        );
        return total + (printAreaObj?.price || 0);
      }, 0);
    } else {
      // Fallback for backward compatibility
      const printArea = (designItem as any).printArea;
      if (printArea) {
        const printAreaObj = printAreas.find((pa) => pa.id === printArea);
        totalPrintAreaPrice = printAreaObj?.price || 0;
      }
    }

    const itemCost = (variantPrice + totalPrintAreaPrice) * quantity;

    return itemCost;
  };

  const updateItemCost = (designIndex: number) => {
    const designs = form.getFieldValue('designs') || [];
    const designItem = designs[designIndex];

    if (designItem) {
      const newItemCost = calculateItemCost(designItem, designIndex);

      if (newItemCost >= 0) {
        // Allow 0 cost items
        // Update the specific design item's itemCost
        designs[designIndex] = {
          ...designItem,
          itemCost: newItemCost,
        };

        form.setFieldsValue({ designs });

        // Recalculate total
        const newTotal = designs.reduce(
          (sum, design) => sum + (design?.itemCost || 0),
          0
        );
        setTotal(newTotal);
      }
    }
  };

  const handleTotal = (_, valuesParam) => {
    const orderItems = [...valuesParam.designs];
    if (orderItems.length === 0) {
      setTotal(0);
      return;
    }

    valuesParam?.designs.forEach((fieldGroup: IOrderItem, index) => {
      if (fieldGroup && fieldGroup?.quantity) {
        // Calculate itemCost based on productTemplateVariant and placement prices
        const calculatedItemCost = calculateItemCost(fieldGroup, index);

        // Always update itemCost with calculated value for new orders or when prices change
        if (calculatedItemCost >= 0) {
          fieldGroup.itemCost = calculatedItemCost;
        } else {
          if (!fieldGroup.itemCost) {
            fieldGroup.itemCost = 0;
          }
        }

        orderItems.splice(index, 1, fieldGroup);
        form.setFieldsValue({ designs: orderItems });
        const tmpTotal = calculateTotal();
        setTotal(tmpTotal);
      }
    });
  };

  // const handleModelChange = () => {
  //   const designs = form.getFieldValue('designs');
  //   if (designs.length > 0) {
  //     designs[currentDesignIdx].printAreaId = phoneModel;
  //     form.setFieldsValue({ designs: designs });
  //   }
  // };

  // const getResendRequestBtnColor = () => {
  //   switch (order?.resendRequest?.status) {
  //     case RequestStatus.PENDING:
  //       return '#1d39c4';
  //     case RequestStatus.APPROVED:
  //       return '#389e0d';
  //     case RequestStatus.REJECTED:
  //       return '#dc143c';
  //   }
  // };

  // const handlePickDesign = (idx: number) => {
  //   setCurrentDesignIdx(idx);
  //   setVisibleModal(true);
  // };

  const toggleMockupEditor = (idx: number) => {
    setShowCanvas((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));

    // Initialize editors visibility with front visible by default
    if (!visibleEditors[idx]) {
      setVisibleEditors((prev) => ({
        ...prev,
        [idx]: { front: true, back: false },
      }));
    }
  };

  const toggleEditor = (designIndex: number, side: 'front' | 'back') => {
    setVisibleEditors((prev) => ({
      ...prev,
      [designIndex]: {
        ...prev[designIndex],
        [side]: !prev[designIndex]?.[side],
      },
    }));
  };

  // Check if all required fields are filled for a design item
  const isDesignItemValid = useCallback(
    (itemIndex: number) => {
      const productTemplateId = form.getFieldValue([
        'designs',
        itemIndex,
        'productTemplateId',
      ]);
      const productTemplateVariantId = form.getFieldValue([
        'designs',
        itemIndex,
        'productTemplateVariantId',
      ]);

      // For the new placement-based structure, we only need product template info to create designs
      return !!(productTemplateId && productTemplateVariantId);
    },
    [form]
  );

  // Effect to update the disabled state of add design button
  // useEffect(() => {
  //   const designs = form.getFieldValue('designs') || [];
  //   const placements = designPlacements[designs.length - 1] || [];
  //   const hasValidProduct = isDesignItemValid(designs.length - 1);
  //   const hasAtLeastOnePlacement = placements.length > 0;
  //   const hasIncompleteItem = hasValidProduct && !hasAtLeastOnePlacement;
  //   setDisabledAddDesign(hasIncompleteItem);
  // }, [order.designs, designPlacements, form, isDesignItemValid]);

  // Validate file type for tracking reference uploads
  const validateTrackingFile = (file: any): boolean => {
    const allowedTypes = [
      'application/pdf',
      'image/png',
      'image/jpeg',
      'image/jpg',
    ];
    const maxSize = 30 * 1024 * 1024; // 30MB

    if (!allowedTypes.includes(file.type)) {
      message.error('Chỉ chấp nhận file PDF, PNG, hoặc JPEG cho mã vận chuyển');
      return false;
    }

    if (file.size > maxSize) {
      message.error('Kích thước file không được vượt quá 30MB');
      return false;
    }

    return true;
  };

  const handleTrackingFileUpload: UploadProps['onChange'] = async (info) => {
    const { fileList: newFileList } = info;

    if (newFileList.length === 0) {
      setTrackingFileList([]);
      return;
    }

    // Get the latest file
    const latestFile = newFileList[newFileList.length - 1];

    if (latestFile.originFileObj) {
      // Validate file before upload
      if (!validateTrackingFile(latestFile.originFileObj)) {
        setTrackingFileList([]);
        return;
      }
      try {
        setUploadingTracking(true);

        // Update file list with uploading status
        const updatedFileList = [
          {
            ...latestFile,
            status: 'uploading' as const,
          },
        ];
        setTrackingFileList(updatedFileList);

        const formData = new FormData();
        formData.append('trackingLabel', latestFile.originFileObj);

        const url = await orderApi.uploadTrackingLabel(formData);

        // Update both form field and backup state with the uploaded file URL
        form.setFieldValue('trackingReference', url);
        setTrackingReferenceUrl(url); // Backup state

        // Update file list with success status
        setTrackingFileList([
          {
            ...latestFile,
            status: 'done' as const,
            url: url,
          },
        ]);

        message.success('Mã vận chuyển đã được tải lên thành công!');
      } catch (error) {
        console.error('Tracking upload error:', error);
        const errorMessage =
          (error as any)?.message ||
          'Tải lên mã vận chuyển thất bại. Vui lòng thử lại.';
        message.error(errorMessage);

        // Update file list with error status
        setTrackingFileList([
          {
            ...latestFile,
            status: 'error' as const,
          },
        ]);

        // Clear backup state on error
        setTrackingReferenceUrl('');
        form.setFieldValue('trackingReference', '');
      } finally {
        setUploadingTracking(false);
      }
    }
  };

  const removeTrackingFile = () => {
    setTrackingFileList([]);
    form.setFieldValue('trackingReference', '');
    setTrackingReferenceUrl(''); // Clear backup state
  };

  const uploadMockup = async (
    designIndex: number,
    mockupDataUrl: string,
    designId?: string
  ) => {
    try {
      setUploadingMockups((prev) => ({ ...prev, [designIndex]: true }));

      // Convert data URL to Blob (single mockup)
      const formData = new FormData();
      const blob = dataURLToBlob(mockupDataUrl);
      formData.append('mockup', blob, `mockup_${designIndex}.png`);

      // Add metadata to associate generated mockup PNG with specific original design
      formData.append('orderId', sellerOrderId || '');
      formData.append('designIndex', designIndex.toString());
      if (designId) {
        formData.append('designId', designId);
      }

      const mockupUrl = await orderApi.uploadMockup(formData);

      // Update the form with single mockup URL
      const currentFormValue: any[] = form.getFieldValue('designs');
      if (currentFormValue[designIndex]) {
        currentFormValue[designIndex] = {
          ...currentFormValue[designIndex],
          mockupUrl: mockupUrl,
        };
        form.setFieldValue('designs', currentFormValue);

        // Also update the order state to persist the mockup URL
        setOrder((prevOrder) => {
          const updatedDesigns = [...(prevOrder.designs || [])] as any[];
          if (updatedDesigns[designIndex]) {
            updatedDesigns[designIndex] = {
              ...updatedDesigns[designIndex],
              mockupUrl: mockupUrl,
            };
          }
          return {
            ...prevOrder,
            designs: updatedDesigns,
          };
        });
      }

      // Update mockupUrls state for immediate UI update
      setMockupUrls((prev) => ({
        ...prev,
        [designIndex]: mockupUrl,
      }));

      message.success('Mockup đã được tải lên thành công!');
      return mockupUrl;
    } catch (error) {
      message.error('Tải lên mockup thất bại. Vui lòng thử lại.');
      throw error;
    } finally {
      setUploadingMockups((prev) => ({ ...prev, [designIndex]: false }));
    }
  };

  const dataURLToBlob = (dataURL: string): Blob => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  // Helper functions for managing placements
  const addPlacement = (
    designIndex: number,
    placement: ICreateOrderDesignPlacement
  ) => {
    setDesignPlacements((prev) => {
      const updatedPlacements = {
        ...prev,
        [designIndex]: [...(prev[designIndex] || []), placement],
      };

      // Recalculate cost immediately with the new placements
      setTimeout(() => {
        // Get current form values
        const designs = form.getFieldValue('designs') || [];
        const designItem = designs[designIndex];

        if (designItem) {
          // Calculate new cost with updated placements
          const newPlacements = updatedPlacements[designIndex] || [];
          let totalPrintAreaPrice = 0;

          if (newPlacements.length > 0) {
            totalPrintAreaPrice = newPlacements.reduce((total, p) => {
              const printAreaObj = printAreas.find(
                (pa) => pa.id === p.printAreaId
              );
              return total + (printAreaObj?.price || 0);
            }, 0);
          }

          // Find variant price
          let variantPrice = 0;
          if (productTemplate?.items && designItem.productTemplateVariantId) {
            for (const template of productTemplate.items) {
              const variant = template.variants?.find(
                (v) => v.id === designItem.productTemplateVariantId
              );
              if (variant) {
                variantPrice = variant.price || 0;
                break;
              }
            }
          }

          const newItemCost =
            (variantPrice + totalPrintAreaPrice) * (designItem.quantity || 1);

          // Update the design item cost
          designs[designIndex] = {
            ...designItem,
            itemCost: newItemCost,
          };

          form.setFieldsValue({ designs });

          // Recalculate total
          const newTotal = designs.reduce(
            (sum, design) => sum + (design?.itemCost || 0),
            0
          );
          setTotal(newTotal);
        }
      }, 10); // Small delay to ensure state is updated

      return updatedPlacements;
    });
  };

  const removePlacement = (designIndex: number, placementIndex: number) => {
    setDesignPlacements((prev) => {
      const currentPlacements = prev[designIndex] || [];
      const updatedPlacements = currentPlacements.filter(
        (_, idx) => idx !== placementIndex
      );

      const newState = {
        ...prev,
        [designIndex]: updatedPlacements,
      };

      // Recalculate cost when placement is removed
      setTimeout(() => {
        // Get current form values
        const designs = form.getFieldValue('designs') || [];
        const designItem = designs[designIndex];

        if (designItem) {
          // Calculate new cost with updated placements
          const newPlacements = updatedPlacements || [];
          let totalPrintAreaPrice = 0;

          if (newPlacements.length > 0) {
            totalPrintAreaPrice = newPlacements.reduce((total, p) => {
              const printAreaObj = printAreas.find(
                (pa) => pa.id === p.printAreaId
              );
              return total + (printAreaObj?.price || 0);
            }, 0);
          }

          // Find variant price
          let variantPrice = 0;
          if (productTemplate?.items && designItem.productTemplateVariantId) {
            for (const template of productTemplate.items) {
              const variant = template.variants?.find(
                (v) => v.id === designItem.productTemplateVariantId
              );
              if (variant) {
                variantPrice = variant.price || 0;
                break;
              }
            }
          }

          const newItemCost =
            (variantPrice + totalPrintAreaPrice) * (designItem.quantity || 1);

          // Update the design item cost
          designs[designIndex] = {
            ...designItem,
            itemCost: newItemCost,
          };

          form.setFieldsValue({ designs });

          // Recalculate total
          const newTotal = designs.reduce(
            (sum, design) => sum + (design?.itemCost || 0),
            0
          );
          setTotal(newTotal);
        }
      }, 10); // Small delay to ensure state is updated

      return newState;
    });
  };

  // Helper function to update a specific placement (for future use)
  // const updatePlacement = (designIndex: number, placementIndex: number, updatedPlacement: Partial<ICreateOrderDesignPlacement>) => {
  //   setDesignPlacements(prev => {
  //     const currentPlacements = [...(prev[designIndex] || [])];
  //     currentPlacements[placementIndex] = {
  //       ...currentPlacements[placementIndex],
  //       ...updatedPlacement,
  //     };
  //
  //     return {
  //       ...prev,
  //       [designIndex]: currentPlacements,
  //     };
  //   });
  //
  //   // Recalculate cost when placement is updated
  //   setTimeout(() => updateItemCost(designIndex), 0);
  // };

  return (
    <>
      {loading && (
        <SpinContainer>
          <Spin size="large" />
        </SpinContainer>
      )}
      <PageContainer>
        {/* <RequestResendModal
          visible={visibleRequestModal}
          onCancel={() => setVisibleRequestModal(false)}
          order={order}
          onConfirm={(request) =>
            setOrder((order) => ({ ...order, resendRequest: request }))
          }
          onApprove={(request) => {
            fetchOrder(id || '');
          }}
          onReject={(request) => {
            setOrder((order) => ({ ...order, resendRequest: request }));
          }}
          onCreateNew={(request) => {
            setVisibleRequestModal(false);
            setOrder((order) => ({ ...order, resendRequest: undefined }));
            setVisibleRequestModal(true);
          }}
        /> */}
        <FlexContainer>
          <PageHeader title="Order Detail" showBackButton />
          {/* {!!order.resendRequest ||
          (currentUser?.role === Role.SELLER &&
            order.status === OrderStatus.SHIPPED) ? (
            <PrimaryButton
              className="font-weight--500 w-30"
              onClick={() => {
                setVisibleRequestModal(true);
              }}
              color={getResendRequestBtnColor()}
            >
              Resend Request{' '}
              {!!order.resendRequest
                ? `(${order.resendRequest?.status})`
                : null}
            </PrimaryButton>
          ) : null} */}
        </FlexContainer>

        <div>
          <CustomForm
            form={form}
            initialValues={order}
            layout="vertical"
            requiredMark="optional"
            onFinish={() => {
              // Show confirmation modal instead of submitting directly
              setConfirmationModalVisible(true);
            }}
            disabled={isFormDisabled}
            onValuesChange={handleTotal}
          >
            {!!id && isAdmin && (
              <Form.Item name="status" label="Status">
                <StyledSelect
                  className="w-100"
                  value={status}
                  onChange={(newValue: any) => {
                    form.setFieldValue('status', newValue);
                  }}
                  options={[
                    { label: 'Tất cả trạng thái', value: '' },
                    { label: 'Chờ xử lý', value: OrderStatus.PENDING },
                    {
                      label: 'Đang xử lý',
                      value: OrderStatus.PRODUCTION_IN_PROGRESS,
                    },
                    { label: 'Đang giao hàng', value: OrderStatus.SHIPPING },
                    { label: 'Đã giao hàng', value: OrderStatus.SHIPPED },
                    { label: 'Đã xử lý', value: OrderStatus.PROCESSED },
                    { label: 'Đã hủy', value: OrderStatus.REFUNDED },
                  ]}
                  disabled={
                    order.status === OrderStatus.CANCELLED ||
                    order.status === OrderStatus.REFUNDED
                  }
                />
              </Form.Item>
            )}
            <Form.Item
              name="orderId"
              label="Mã đơn hàng"
              rules={[
                {
                  required: true,
                  type: 'string',
                  message: 'Vui lòng nhập mã đơn hàng',
                },
              ]}
            >
              <CustomInput
                placeholder="Mã đơn hàng"
                onChange={(e) => {
                  setSellerOrderId(e.target.value);
                }}
              />
            </Form.Item>
            <Form.Item
              name="trackingCode"
              label="Mã vận đơn"
              rules={[
                {
                  pattern: /^[A-Za-z0-9\-_]*$/,
                  message:
                    'Mã vận đơn chỉ được chứa chữ cái, số, dấu gạch ngang và gạch dưới',
                },
                {
                  max: 50,
                  message: 'Mã vận đơn không được vượt quá 50 ký tự',
                },
              ]}
              required
            >
              <CustomInput
                placeholder="Nhập mã vận đơn"
                maxLength={50}
                suffix={
                  form.getFieldValue('trackingCode') && (
                    <Button
                      type="text"
                      size="small"
                      onClick={() => {
                        const trackingCode = form.getFieldValue('trackingCode');
                        if (trackingCode) {
                          navigator.clipboard.writeText(trackingCode);
                          message.success('Đã sao chép mã vận đơn!');
                        }
                      }}
                      style={{ padding: '0 4px' }}
                    >
                      Sao chép
                    </Button>
                  )
                }
              />
            </Form.Item>
            <Form.Item name="address" label="Địa chỉ">
              <CustomTextArea
                autoSize={{ minRows: 3, maxRows: 4 }}
                placeholder="Nhập địa chỉ đầy đủ"
                maxLength={1000}
              />
            </Form.Item>
            <Form.Item name="notes" label="Ghi chú">
              <CustomTextArea
                autoSize={{ minRows: 3, maxRows: 4 }}
                placeholder="Nhập ghi chú"
                maxLength={1000}
              />
            </Form.Item>
            <Form.Item name="internalNotes" label="Ghi chú nội bộ">
              <CustomTextArea
                autoSize={{ minRows: 3, maxRows: 4 }}
                placeholder="Nhập ghi chú nội bộ"
                maxLength={1000}
              />
            </Form.Item>
            <Form.Item
              name="platform"
              label="Nền tảng"
              rules={[{ required: true, message: 'Vui lòng chọn nền tảng' }]}
            >
              <StyledSelect
                className="w-100"
                options={Object.values(Platform).map((p) => ({
                  label: p,
                  value: p,
                }))}
              />
            </Form.Item>
            <Form.Item label="Độ ưu tiên" name="orderType">
              <Checkbox
                checked={
                  form.getFieldValue('orderType') === OrderType.HIGH_PRIORITY
                }
                onChange={(e) => {
                  form.setFieldValue(
                    'orderType',
                    e.target.checked
                      ? OrderType.HIGH_PRIORITY
                      : OrderType.NORMAL
                  );
                }}
              >
                Đặc biệt (Đơn hàng có độ ưu tiên cao, sẽ được xử lý trước)
              </Checkbox>
            </Form.Item>
            {/* Always show tracking reference field during creation, for admins during edit, or for pending orders */}
            {(!id || isAdmin || (!!id && status === OrderStatus.PENDING)) && (
              <Form.Item
                name="trackingReference"
                label="Mã vận chuyển"
                rules={[
                  {
                    required: !id || status === OrderStatus.PENDING,
                    message: 'Vui lòng tải lên mã vận chuyển',
                  },
                  {
                    validator: (_, value) => {
                      if (
                        !value &&
                        trackingFileList.length === 0 &&
                        trackingReferenceUrl === ''
                      ) {
                        return Promise.reject(
                          new Error('Vui lòng tải lên mã vận chuyển')
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <div>
                  <Upload
                    name="trackingLabel"
                    fileList={trackingFileList}
                    onChange={handleTrackingFileUpload}
                    onRemove={removeTrackingFile}
                    maxCount={1}
                    accept=".pdf,.png,.jpg,.jpeg"
                    disabled={isFormDisabled || uploadingTracking}
                    beforeUpload={() => false} // Prevent auto upload
                  >
                    {trackingFileList.length === 0 && (
                      <Button
                        icon={<UploadOutlined />}
                        loading={uploadingTracking}
                        disabled={isFormDisabled}
                      >
                        Tải lên mã vận chuyển
                      </Button>
                    )}
                  </Upload>
                  {trackingFileList.length > 0 && (
                    <div
                      style={{ marginTop: 8, fontSize: '12px', color: '#666' }}
                    >
                      Mã vận chuyển đã được tải lên thành công
                    </div>
                  )}
                </div>
              </Form.Item>
            )}
            {/* Show tracking reference as read-only for existing orders when user is not admin and order is not pending */}
            {!!id &&
              !isAdmin &&
              order?.trackingReference &&
              status !== OrderStatus.PENDING && (
                <Form.Item label="Mã vận chuyển">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span>Đã có mã vận chuyển</span>
                    <Button
                      type="link"
                      onClick={() =>
                        window.open(order.trackingReference, '_blank')
                      }
                      style={{ padding: 0 }}
                    >
                      Xem mã vận chuyển
                    </Button>
                  </div>
                </Form.Item>
              )}
            <Divider />
            <p
              style={{
                fontSize: '16px',
                fontWeight: 'bold',
                marginTop: '14px',
                marginBottom: '14px',
              }}
            >
              Thiết kế
            </p>
            <Form.List name="designs">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={name}>
                      <CreateOrderItemWrapper>
                        <ImageContainer>
                          {/* Show mockup if available, otherwise show original design */}
                          {mockupUrls[name] ? (
                            <img
                              src={mockupUrls[name]}
                              alt="T-shirt mockup preview"
                            />
                          ) : !!listDesign[name] ? (
                            <img src={listDesign[name]} alt="Design preview" />
                          ) : null}
                          {uploadingMockups[name] && (
                            <div
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '12px',
                                borderRadius: '4px',
                              }}
                            >
                              <Spin size="small" style={{ marginRight: 8 }} />
                              Đang tải mockup...
                            </div>
                          )}
                          {/* Mockup indicator */}
                          {mockupUrls[name] && (
                            <div
                              style={{
                                position: 'absolute',
                                bottom: 4,
                                right: 4,
                                backgroundColor: '#52c41a',
                                color: 'white',
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '10px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓ Mockup
                            </div>
                          )}
                        </ImageContainer>
                        <Form.Item hidden name="id" label="Id">
                          <CustomInput placeholder="Id" />
                        </Form.Item>
                        <Form.Item
                          name={[name, 'productTemplateId']}
                          label="Sản phẩm/Màu sắc/Form"
                        >
                          <StyledSelect
                            className="w-100"
                            placeholder="Sản phẩm/Màu sắc/Form"
                            options={(() => {
                              const items = productTemplate?.items || [];
                              const tshirtItems = items.filter(
                                (pt) => pt.type === ProductType.TSHIRT
                              );
                              const bagItems = items.filter(
                                (pt) => pt.type === ProductType.BAG
                              );

                              const createOption = (pt: (typeof items)[0]) => ({
                                label: (
                                  <Space>
                                    <div
                                      style={{
                                        width: 20,
                                        height: 20,
                                        backgroundColor: pt.colorHexCode,
                                        border: '1px solid #d9d9d9',
                                        borderRadius: 4,
                                      }}
                                    />
                                    {pt.color} - {pt.form}
                                  </Space>
                                ),
                                value: pt.id,
                              });

                              type OptionGroup = {
                                label: React.ReactNode;
                                options: {
                                  label: React.ReactNode;
                                  value: string;
                                }[];
                              };
                              const groups: OptionGroup[] = [];
                              if (tshirtItems.length > 0) {
                                groups.push({
                                  label: (
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        color: '#1890ff',
                                      }}
                                    >
                                      👕 Áo
                                    </span>
                                  ),
                                  options: tshirtItems.map(createOption),
                                });
                              }
                              if (bagItems.length > 0) {
                                groups.push({
                                  label: (
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        color: '#52c41a',
                                      }}
                                    >
                                      👜 Túi
                                    </span>
                                  ),
                                  options: bagItems.map(createOption),
                                });
                              }
                              return groups;
                            })()}
                            onChange={(value) => {
                              const selectedProductTemplate =
                                productTemplate?.items?.find(
                                  (pt) => pt.id === value
                                );
                              if (selectedProductTemplate) {
                                setProductTemplateVariants(
                                  selectedProductTemplate.variants
                                );
                              }

                              // Update the form field first
                              const currentDesigns =
                                form.getFieldValue('designs') || [];
                              currentDesigns[name] = {
                                ...currentDesigns[name],
                                productTemplateId: value,
                                // Clear the variant selection when color changes
                                productTemplateVariantId: undefined,
                              };
                              form.setFieldsValue({
                                designs: currentDesigns,
                              });

                              // Recalculate item cost after clearing variant
                              setTimeout(() => updateItemCost(name), 0);
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'productTemplateVariantId']}
                          label="Kích thước"
                        >
                          <StyledSelect
                            className="w-100"
                            options={(() => {
                              // Get variants for the specific product template of this design
                              const currentProductTemplateId =
                                form.getFieldValue([
                                  'designs',
                                  name,
                                  'productTemplateId',
                                ]);
                              const currentProductTemplate =
                                productTemplate?.items?.find(
                                  (pt) => pt.id === currentProductTemplateId
                                );
                              const variants =
                                currentProductTemplate?.variants ||
                                productTemplateVariants ||
                                [];
                              return variants.map((ptv) => ({
                                label: ProductSizeMap[ptv.size],
                                value: ptv.id,
                              }));
                            })()}
                            placeholder="Chọn kích thước"
                            disabled={
                              !form.getFieldValue([
                                'designs',
                                name,
                                'productTemplateId',
                              ]) || isFormDisabled
                            }
                            onChange={(value) => {
                              // Update the form field first
                              const currentDesigns =
                                form.getFieldValue('designs') || [];
                              currentDesigns[name] = {
                                ...currentDesigns[name],
                                productTemplateVariantId: value,
                              };
                              form.setFieldsValue({
                                designs: currentDesigns,
                              });

                              // Then recalculate item cost
                              setTimeout(() => updateItemCost(name), 0);
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'quantity']}
                          rules={[
                            {
                              required: true,
                              message: '',
                            },
                          ]}
                          label="Số lượng"
                        >
                          <StyledInputNumber
                            min={1}
                            max={1000000}
                            placeholder="Số lượng"
                            step={1}
                            className="w-100"
                            onChange={(value) => {
                              // Update the form field first
                              const currentDesigns =
                                form.getFieldValue('designs') || [];
                              currentDesigns[name] = {
                                ...currentDesigns[name],
                                quantity: value,
                              };
                              form.setFieldsValue({
                                designs: currentDesigns,
                              });

                              // Then recalculate item cost
                              setTimeout(() => updateItemCost(name), 0);
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'itemCost']}
                          label="Giá"
                        >
                          <StyledInputNumber className="w-100" disabled />
                        </Form.Item>

                        {/* Placements Management */}
                        <div style={{ marginBottom: '14px' }}>
                          <label
                            style={{
                              fontSize: '14px',
                              fontWeight: 'bold',
                              marginBottom: '12px',
                              display: 'block',
                              color: '#333',
                            }}
                          >
                            📍 Vị trí thiết kế (
                            {designPlacements[name]?.length || 0} vị trí)
                            {designPlacements[name]?.length > 0 && (
                              <span
                                style={{
                                  fontSize: '12px',
                                  color: '#666',
                                  marginLeft: '8px',
                                }}
                              >
                                (Trước:{' '}
                                {designPlacements[name]?.filter(
                                  (p) => p.side === Side.FRONT
                                ).length || 0}
                                , Sau:{' '}
                                {designPlacements[name]?.filter(
                                  (p) => p.side === Side.BACK
                                ).length || 0}
                                )
                              </span>
                            )}
                          </label>
                          {designPlacements[name]?.map(
                            (placement, placementIndex) => (
                              <div
                                key={placementIndex}
                                style={{
                                  border: `2px solid ${
                                    placement.side === Side.FRONT
                                      ? '#1890ff'
                                      : '#52c41a'
                                  }`,
                                  padding: '12px',
                                  borderRadius: '8px',
                                  backgroundColor:
                                    placement.side === Side.FRONT
                                      ? '#f0f8ff'
                                      : '#f6ffed',
                                  alignItems: 'center',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: '13px',
                                    fontWeight: '500',
                                  }}
                                >
                                  <span
                                    style={{
                                      color:
                                        placement.side === Side.FRONT
                                          ? '#1890ff'
                                          : '#52c41a',
                                      fontWeight: 'bold',
                                      marginRight: '8px',
                                    }}
                                  >
                                    {placement.side === Side.FRONT
                                      ? '🔵 Mặt trước'
                                      : '🟢 Mặt sau'}
                                  </span>
                                  <span style={{ color: '#666' }}>|</span>
                                  <strong style={{ marginLeft: '8px' }}>
                                    Vùng in:
                                  </strong>{' '}
                                  <span style={{ color: '#333' }}>
                                    {printAreas.find(
                                      (pa) => pa.id === placement.printAreaId
                                    )?.name || placement.printAreaId}
                                  </span>
                                  {placement.mockupUrl && (
                                    <span
                                      style={{
                                        color: '#52c41a',
                                        marginLeft: '8px',
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      ✓ Có mockup
                                    </span>
                                  )}
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <Button
                                    size="small"
                                    danger
                                    onClick={() =>
                                      removePlacement(name, placementIndex)
                                    }
                                    style={{
                                      borderRadius: '6px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    Xóa
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      window.open(placement.mockupUrl, '_blank')
                                    }
                                    style={{
                                      borderRadius: '6px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    Xem mockup
                                  </Button>
                                </div>
                              </div>
                            )
                          )}

                          {(designPlacements[name]?.length || 0) === 0 && (
                            <div
                              style={{
                                padding: '12px',
                                textAlign: 'center',
                                color: '#999',
                                fontSize: '12px',
                                fontStyle: 'italic',
                                border: '1px dashed #d9d9d9',
                                borderRadius: '4px',
                              }}
                            >
                              Chưa có thiết kế nào được thêm vào sản phẩm này
                            </div>
                          )}
                        </div>
                        {/* <PrimaryButton
                              onClick={() => {
                                handlePickDesign(name);
                              }}
                              className="w-100 m__b--14"
                            >
                              Chọn thiết kế
                            </PrimaryButton> */}
                        <div>
                          <div
                            style={{
                              display: 'flex',
                              gap: '8px',
                              marginBottom:
                                !showCanvas[name] && !isDesignItemValid(name)
                                  ? '4px'
                                  : '14px',
                            }}
                          >
                            <PrimaryButton
                              onClick={() => {
                                toggleMockupEditor(name);
                              }}
                              className="w-100"
                              disabled={
                                (!showCanvas[name] &&
                                  !isDesignItemValid(name)) ||
                                !!mockupUrls[name]
                              }
                            >
                              {showCanvas[name] ? 'Đóng Mockup' : 'Mockup'}
                            </PrimaryButton>
                          </div>
                          {!mockupUrls[name] &&
                            !showCanvas[name] &&
                            !isDesignItemValid(name) && (
                              <div
                                style={{
                                  fontSize: '11px',
                                  color: '#999',
                                  marginBottom: '14px',
                                  fontStyle: 'italic',
                                }}
                              >
                                Vui lòng chọn màu sắc và kích thước trước
                              </div>
                            )}
                        </div>
                        <MinusCircleOutlined
                          style={{
                            marginBottom: '30px',
                          }}
                          onClick={() => {
                            remove(name);
                            const tmpDesign = cloneDeep(listDesign);
                            tmpDesign.splice(name, 1);
                            setListDesign(tmpDesign);

                            // Clean up canvas state and mockup URLs for removed item
                            setShowCanvas((prev) => {
                              const newState = { ...prev };
                              delete newState[name];
                              // Shift indices for items after the removed one
                              const shiftedState: Record<number, boolean> = {};
                              Object.keys(newState).forEach((key) => {
                                const index = parseInt(key);
                                if (index > name) {
                                  shiftedState[index - 1] = newState[index];
                                } else {
                                  shiftedState[index] = newState[index];
                                }
                              });
                              return shiftedState;
                            });

                            // Clean up mockup URLs for removed item
                            setMockupUrls((prev) => {
                              const newState = { ...prev };
                              delete newState[name];
                              // Shift indices for items after the removed one
                              const shiftedState: Record<number, string> = {};
                              Object.keys(newState).forEach((key) => {
                                const index = parseInt(key);
                                if (index > name) {
                                  shiftedState[index - 1] = newState[index];
                                } else {
                                  shiftedState[index] = newState[index];
                                }
                              });
                              return shiftedState;
                            });

                            // Clean up placement data for removed item
                            setDesignPlacements((prev) => {
                              const newState = { ...prev };
                              delete newState[name];
                              // Shift indices for items after the removed one
                              const shiftedState: Record<
                                number,
                                ICreateOrderDesignPlacement[]
                              > = {};
                              Object.keys(newState).forEach((key) => {
                                const index = parseInt(key);
                                if (index > name) {
                                  shiftedState[index - 1] = newState[index];
                                } else {
                                  shiftedState[index] = newState[index];
                                }
                              });
                              return shiftedState;
                            });

                            // Clean up editor visibility for removed item
                            setVisibleEditors((prev) => {
                              const newState = { ...prev };
                              delete newState[name];
                              // Shift indices for items after the removed one
                              const shiftedState: Record<
                                number,
                                { front: boolean; back: boolean }
                              > = {};
                              Object.keys(newState).forEach((key) => {
                                const index = parseInt(key);
                                if (index > name) {
                                  shiftedState[index - 1] = newState[index];
                                } else {
                                  shiftedState[index] = newState[index];
                                }
                              });
                              return shiftedState;
                            });

                            if (currentDesignIdx === name) {
                              setCurrentDesignIdx(0);
                            } else if (name < currentDesignIdx) {
                              setCurrentDesignIdx(currentDesignIdx - 1);
                            }
                          }}
                        />
                      </CreateOrderItemWrapper>
                      {showCanvas[name] && (
                        <div
                          style={{
                            marginTop: '20px',
                            backgroundColor: '#fafafa',
                            padding: '20px',
                            borderRadius: '8px',
                          }}
                        >
                          <div
                            style={{
                              marginBottom: '20px',
                              textAlign: 'center',
                            }}
                          >
                            <h4 style={{ marginBottom: '16px' }}>
                              Thêm thiết kế cho sản phẩm
                            </h4>

                            {/* Toggle buttons for front/back editors */}
                            <div
                              style={{
                                display: 'flex',
                                gap: '12px',
                                justifyContent: 'center',
                              }}
                            >
                              <Button
                                type={
                                  visibleEditors[name]?.front
                                    ? 'primary'
                                    : 'default'
                                }
                                onClick={() => toggleEditor(name, 'front')}
                                style={{
                                  borderColor: '#1890ff',
                                  color: visibleEditors[name]?.front
                                    ? '#fff'
                                    : '#1890ff',
                                }}
                              >
                                🔵 Mặt trước
                              </Button>
                              <Button
                                type={
                                  visibleEditors[name]?.back
                                    ? 'primary'
                                    : 'default'
                                }
                                onClick={() => toggleEditor(name, 'back')}
                                style={{
                                  borderColor: '#52c41a',
                                  color: visibleEditors[name]?.back
                                    ? '#fff'
                                    : '#52c41a',
                                  backgroundColor: visibleEditors[name]?.back
                                    ? '#52c41a'
                                    : undefined,
                                }}
                              >
                                🟢 Mặt sau
                              </Button>
                            </div>
                          </div>

                          {/* Front Side Editor */}
                          {visibleEditors[name]?.front && (
                            <div
                              style={{
                                marginBottom: '30px',
                                border: '2px solid #1890ff',
                                borderRadius: '8px',
                                padding: '16px',
                                backgroundColor: '#fff',
                              }}
                            >
                              <h5
                                style={{
                                  marginBottom: '16px',
                                  color: '#1890ff',
                                  textAlign: 'center',
                                }}
                              >
                                🔵 Mặt trước
                              </h5>
                              <TShirtMockupEditor
                                templateUrl={
                                  productTemplate?.items?.find(
                                    (pt) =>
                                      pt.id ===
                                      form.getFieldValue([
                                        'designs',
                                        name,
                                        'productTemplateId',
                                      ])
                                  )?.frontTemplateUrl ?? ''
                                }
                                orderId={sellerOrderId ?? ''}
                                productType={
                                  productTemplate?.items?.find(
                                    (pt) =>
                                      pt.id ===
                                      form.getFieldValue([
                                        'designs',
                                        name,
                                        'productTemplateId',
                                      ])
                                  )?.type
                                }
                                color={
                                  productTemplate?.items?.find(
                                    (pt) =>
                                      pt.id ===
                                      form.getFieldValue([
                                        'designs',
                                        name,
                                        'productTemplateId',
                                      ])
                                  )?.color
                                }
                                onDesignSubmitted={async (
                                  design,
                                  mockupDataUrl,
                                  selectedPrintArea,
                                  selectedSide
                                ) => {
                                  // Create a new placement for this design (force front side)
                                  const newPlacement: ICreateOrderDesignPlacement =
                                    {
                                      designId: design.id || '',
                                      side: Side.FRONT,
                                      printAreaId:
                                        selectedPrintArea ||
                                        PrintAreaType.CenterChest,
                                      mockupUrl: '', // Will be set after upload
                                    };

                                  // Upload generated mockup PNG if available
                                  if (mockupDataUrl) {
                                    try {
                                      const uploadedMockupUrl =
                                        await uploadMockup(
                                          name,
                                          mockupDataUrl,
                                          design.id
                                        );
                                      newPlacement.mockupUrl =
                                        uploadedMockupUrl;
                                      message.success(
                                        '✅ Thiết kế mặt trước đã được lưu thành công!'
                                      );
                                    } catch (error) {
                                      message.warning(
                                        '⚠️ Thiết kế mặt trước đã được lưu nhưng mockup PNG không tải được. Vui lòng thử lại.'
                                      );
                                    }
                                  } else {
                                    message.success(
                                      '✅ Thiết kế mặt trước đã được lưu thành công!'
                                    );
                                  }

                                  // Add the placement to the design
                                  addPlacement(name, newPlacement);
                                }}
                              />
                            </div>
                          )}

                          {/* Back Side Editor */}
                          {visibleEditors[name]?.back && (
                            <div
                              style={{
                                marginBottom: '20px',
                                border: '2px solid #52c41a',
                                borderRadius: '8px',
                                padding: '16px',
                                backgroundColor: '#fff',
                              }}
                            >
                              <h5
                                style={{
                                  marginBottom: '16px',
                                  color: '#52c41a',
                                  textAlign: 'center',
                                }}
                              >
                                🟢 Mặt sau
                              </h5>
                              <TShirtMockupEditor
                                templateUrl={
                                  productTemplate?.items?.find(
                                    (pt) =>
                                      pt.id ===
                                      form.getFieldValue([
                                        'designs',
                                        name,
                                        'productTemplateId',
                                      ])
                                  )?.backTemplateUrl ?? ''
                                }
                                side={Side.BACK}
                                orderId={sellerOrderId ?? ''}
                                productType={
                                  productTemplate?.items?.find(
                                    (pt) =>
                                      pt.id ===
                                      form.getFieldValue([
                                        'designs',
                                        name,
                                        'productTemplateId',
                                      ])
                                  )?.type
                                }
                                color={
                                  productTemplate?.items?.find(
                                    (pt) =>
                                      pt.id ===
                                      form.getFieldValue([
                                        'designs',
                                        name,
                                        'productTemplateId',
                                      ])
                                  )?.color
                                }
                                onDesignSubmitted={async (
                                  design,
                                  mockupDataUrl,
                                  selectedPrintArea,
                                  selectedSide
                                ) => {
                                  // Create a new placement for this design (force back side)
                                  const newPlacement: ICreateOrderDesignPlacement =
                                    {
                                      designId: design.id || '',
                                      side: Side.BACK,
                                      printAreaId:
                                        selectedPrintArea ||
                                        PrintAreaType.CenterChest,
                                      mockupUrl: '', // Will be set after upload
                                    };

                                  // Upload generated mockup PNG if available
                                  if (mockupDataUrl) {
                                    try {
                                      const uploadedMockupUrl =
                                        await uploadMockup(
                                          name,
                                          mockupDataUrl,
                                          design.id
                                        );
                                      newPlacement.mockupUrl =
                                        uploadedMockupUrl;
                                      message.success(
                                        '✅ Thiết kế mặt sau đã được lưu thành công!'
                                      );
                                    } catch (error) {
                                      message.warning(
                                        '⚠️ Thiết kế mặt sau đã được lưu nhưng mockup PNG không tải được. Vui lòng thử lại.'
                                      );
                                    }
                                  } else {
                                    message.success(
                                      '✅ Thiết kế mặt sau đã được lưu thành công!'
                                    );
                                  }

                                  // Add the placement to the design
                                  addPlacement(name, newPlacement);
                                }}
                              />
                            </div>
                          )}

                          {/* Close button */}
                          <div
                            style={{ textAlign: 'center', marginTop: '20px' }}
                          >
                            <Button
                              onClick={() =>
                                setShowCanvas((prev) => ({
                                  ...prev,
                                  [name]: false,
                                }))
                              }
                              style={{ minWidth: '120px' }}
                              type="default"
                            >
                              Đóng trình chỉnh sửa
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  <Form.Item>
                    <Tooltip
                      // title={(() => {
                      //   const currentDesigns =
                      //     form.getFieldValue('designs') || [];
                      //   const hasIncompleteItem = currentDesigns.some(
                      //     (_, index) => {
                      //       const placements = designPlacements[index] || [];
                      //       const hasValidProduct = isDesignItemValid(index);
                      //       const hasAtLeastOnePlacement =
                      //         placements.length > 0;
                      //       return hasValidProduct && !hasAtLeastOnePlacement;
                      //     }
                      //   );
                      //   return hasIncompleteItem
                      //     ? 'Vui lòng hoàn thành sản phẩm hiện tại trước khi thêm mẫu mới (cần ít nhất 1 thiết kế)'
                      //     : 'Thêm sản phẩm mới';
                      // })()}
                      title="Thêm sản phẩm mới"
                    >
                      <Button
                        type="dashed"
                        onClick={() => {
                          add({ quantity: 1, itemCost: 0, placements: [] });
                          const tmpDesign = cloneDeep(listDesign);
                          tmpDesign.push('');
                          setListDesign(tmpDesign);

                          // Initialize placement state for new design item
                          const currentDesigns =
                            form.getFieldValue('designs') || [];
                          const newIndex = currentDesigns.length; // This will be the index of the newly added item
                          setDesignPlacements((prev) => ({
                            ...prev,
                            [newIndex]: [],
                          }));

                          // Initialize editor visibility for new design item (front visible by default)
                          setVisibleEditors((prev) => ({
                            ...prev,
                            [newIndex]: { front: false, back: false },
                          }));

                          // setDisabledAddDesign(true);
                        }}
                        block
                        icon={<PlusOutlined />}
                        disabled={disabledAddDesign}
                      >
                        Thêm mẫu
                      </Button>
                    </Tooltip>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <CheckoutDraftRow>
              <label>Total</label>
              <p>{total.toLocaleString('vi-VN')} VNĐ</p>
            </CheckoutDraftRow>

            <Form.Item>
              <ButtonGroupContainer>
                {!!id && status === OrderStatus.PENDING && (
                  <PrimaryButton
                    disabled={loading}
                    style={{ background: '#e7d4d4' }}
                    className="text-danger"
                    htmlType="button"
                    onClick={deleteOrder}
                  >
                    <span className="font-size--14">Remove</span>
                  </PrimaryButton>
                )}
                {!!id && order?.trackingReference && isAdmin && (
                  <PrimaryButton
                    disabled={loading}
                    style={{ background: '#4096ff' }}
                    htmlType="button"
                    onClick={() =>
                      window.open(order.trackingReference, '_blank')
                    }
                  >
                    <span className="font-size--14">View Tracking Label</span>
                  </PrimaryButton>
                )}
                <PrimaryButton
                  htmlType="submit"
                  disabled={
                    !form.getFieldValue('designs')?.length ||
                    (!isAdmin && status !== OrderStatus.PENDING) ||
                    order.status === OrderStatus.CANCELLED ||
                    order.status === OrderStatus.REFUNDED
                  }
                >
                  Save
                </PrimaryButton>
              </ButtonGroupContainer>
            </Form.Item>
          </CustomForm>
        </div>
      </PageContainer>
      <SearchDesignModal
        visible={visibleModal}
        printAreas={printAreas}
        onCancel={() => setVisibleModal(false)}
        onSelect={(design: IDesign) => {
          const currentFormValue: any[] = form.getFieldValue('designs');
          currentFormValue[currentDesignIdx] = {
            ...currentFormValue[currentDesignIdx],
            designId: design.id || '',
          };
          form.setFieldValue('designs', currentFormValue);
          const tmp = cloneDeep(listDesign);
          if (!design.designUrl) {
            return;
          }

          tmp[currentDesignIdx] = design.thumbnailUrl ?? design.designUrl;
          setListDesign(tmp);
        }}
      />

      {/* Order Confirmation Modal */}
      <ConfirmModal
        visible={confirmationModalVisible}
        onOk={() => {
          setConfirmationModalVisible(false);
          // Process the form submission with the saved values
          if (!!id) {
            updateOrder();
          } else {
            createOrder();
          }
        }}
        onCancel={() => {
          setConfirmationModalVisible(false);
          // setPendingFormValues(null);
        }}
        title="Xác nhận đơn hàng"
        okText="Xác nhận"
        cancelText="Hủy"
      >
        <p>Bạn có chắc chắn muốn {!!id ? 'cập nhật' : 'tạo'} đơn hàng này?</p>
        <p>Vui lòng kiểm tra tất cả các chi tiết trước khi xác nhận.</p>
      </ConfirmModal>
    </>
  );
};
export default OrderDetail;
